import { useState, useEffect } from 'react'
import { FiX, FiLoader, FiSave, FiSettings, FiMousePointer } from 'react-icons/fi'
import axios from 'axios'
import toast from 'react-hot-toast'

export default function SectionFormStylingModal({ isOpen, onClose, website, onSuccess }) {
  const [activeTab, setActiveTab] = useState('form') // 'form' or 'button'
  const [formData, setFormData] = useState({
    // Form styling
    backgroundColor: '#FFFFFF',
    textColor: '#111827',
    hoverBackgroundColor: '#F9FAFB',
    hoverTextColor: '#111827',
    selectedFormSectionTypes: [],
    selectedFormSectionIds: [],
    // Button styling
    buttonBackgroundColor: '#3B82F6',
    buttonTextColor: '#FFFFFF',
    hoverButtonBackgroundColor: '#2563EB',
    hoverButtonTextColor: '#FFFFFF',
    selectedButtonSectionTypes: [],
    selectedButtonSectionIds: []
  })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (website && isOpen) {
      const existingFormTypes = (website.sectionFormStyling?.selectedFormSectionTypes ?? website.sectionFormStyling?.selectedSectionTypes) || []
      const existingButtonTypes = (website.sectionFormStyling?.selectedButtonSectionTypes ?? website.sectionFormStyling?.selectedSectionTypes) || []

      setFormData({
        backgroundColor: website.sectionFormStyling?.backgroundColor || '#FFFFFF',
        textColor: website.sectionFormStyling?.textColor || '#111827',
        hoverBackgroundColor: website.sectionFormStyling?.hoverBackgroundColor || '#F9FAFB',
        hoverTextColor: website.sectionFormStyling?.hoverTextColor || '#111827',
        selectedFormSectionTypes: existingFormTypes,
        selectedFormSectionIds: website.sectionFormStyling?.selectedFormSectionIds || [],
        buttonBackgroundColor: website.sectionFormStyling?.buttonBackgroundColor || '#3B82F6',
        buttonTextColor: website.sectionFormStyling?.buttonTextColor || '#FFFFFF',
        hoverButtonBackgroundColor: website.sectionFormStyling?.hoverButtonBackgroundColor || '#2563EB',
        hoverButtonTextColor: website.sectionFormStyling?.hoverButtonTextColor || '#FFFFFF',
        selectedButtonSectionTypes: existingButtonTypes,
        selectedButtonSectionIds: website.sectionFormStyling?.selectedButtonSectionIds || []
      })
    }
  }, [website, isOpen])

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSelectionChange = (field, value, checked) => {
    setFormData((prev) => ({
      ...prev,
      [field]: checked
        ? [...prev[field], value]
        : prev[field].filter(item => item !== value)
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)

    try {
      await axios.put(`/websites/${website._id}/section-form-styling`, formData)
      toast.success('Section form styling updated successfully')
      if (onSuccess) onSuccess()
      onClose()
    } catch (error) {
      console.error('Error updating section form styling:', error)
      toast.error(error.response?.data?.message || 'Failed to update section form styling')
    } finally {
      setSaving(false)
    }
  }

  const sectionTypeOptions = [
    { value: 'contact', label: 'Contact Forms' },
    { value: 'reviews', label: 'Review Forms' },
    { value: 'resume', label: 'Resume Upload Forms' },
    { value: 'products', label: 'Product Forms' },
    { value: 'tickets', label: 'Ticket Forms' },
    { value: 'crypto-product', label: 'Crypto Product Forms' },
    { value: 'fixtures', label: 'Fixtures Sections' },
    { value: 'points-table', label: 'Points Table Sections' },
    { value: 'checkout', label: 'Checkout Forms' },
    { value: 'report', label: 'Report Modals' }
  ]

  // Extendable section types to include requested new types
  const extendedSectionTypes = [
    { value: 'team', label: 'Team Section' },
    { value: 'section-items', label: 'Section Items' },
    { value: 'left-sidebar', label: 'Left Sidebar' },
    { value: 'right-sidebar', label: 'Right Sidebar' },
    { value: 'audio', label: 'Audio Section' },
    { value: 'shopping-cart', label: 'Shopping Cart' },
    { value: 'cart', label: 'Cart (alias)' },
    { value: 'team-members', label: 'Team Members (alias)' },
    { value: 'pagination-controls', label: 'Pagination Controls' },
    { value: 'image-gallery-modal', label: 'Image Gallery Modal' }
  ]

  const combinedSectionTypeOptions = [...sectionTypeOptions, ...extendedSectionTypes]

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
              <span className="text-blue-600 text-lg">🎨</span>
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">Section Form Styling</h2>
              <p className="text-sm text-gray-500">Apply a polished style across selected section forms.</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
            aria-label="Close styling modal"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[calc(100vh-8rem)] overflow-y-auto">
          {/* Tab Navigation */}
          <div className="flex border-b border-gray-200">
            <button
              type="button"
              onClick={() => setActiveTab('form')}
              className={`flex items-center gap-2 px-4 py-3 font-medium text-sm transition-colors ${
                activeTab === 'form'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <FiSettings className="w-4 h-4" />
              Form Styling
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('button')}
              className={`flex items-center gap-2 px-4 py-3 font-medium text-sm transition-colors ${
                activeTab === 'button'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <FiMousePointer className="w-4 h-4" />
              Button Styling
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'form' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Customize section form appearance</h3>
                <p className="text-sm text-gray-600 mb-6">
                  Set a global background and text color for section forms on your published website.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Background Color</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={formData.backgroundColor}
                    onChange={(e) => handleInputChange('backgroundColor', e.target.value)}
                    className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.backgroundColor}
                    onChange={(e) => handleInputChange('backgroundColor', e.target.value)}
                    placeholder="#FFFFFF"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Set the default background color for section forms.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Text Color</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={formData.textColor}
                    onChange={(e) => handleInputChange('textColor', e.target.value)}
                    className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.textColor}
                    onChange={(e) => handleInputChange('textColor', e.target.value)}
                    placeholder="#111827"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Set the default text color for section forms.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Hover Background Color</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={formData.hoverBackgroundColor}
                    onChange={(e) => handleInputChange('hoverBackgroundColor', e.target.value)}
                    className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.hoverBackgroundColor}
                    onChange={(e) => handleInputChange('hoverBackgroundColor', e.target.value)}
                    placeholder="#F9FAFB"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Set the background color when hovering over section forms.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Hover Text Color</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={formData.hoverTextColor}
                    onChange={(e) => handleInputChange('hoverTextColor', e.target.value)}
                    className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.hoverTextColor}
                    onChange={(e) => handleInputChange('hoverTextColor', e.target.value)}
                    placeholder="#111827"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Set the text color when hovering over section forms.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Apply form styling to section types</label>
                <p className="text-sm text-gray-500 mb-4">Choose which section types should inherit the form background and text colors.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {combinedSectionTypeOptions.map(({ value, label }) => (
                    <label
                      key={`form-${value}`}
                      className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 hover:border-blue-300 transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={formData.selectedFormSectionTypes.includes(value)}
                        onChange={(e) => handleSelectionChange('selectedFormSectionTypes', value, e.target.checked)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'button' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Customize section form buttons</h3>
                <p className="text-sm text-gray-600 mb-6">
                  Set custom colors for buttons within section forms on your published website.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Button Background Color</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={formData.buttonBackgroundColor}
                    onChange={(e) => handleInputChange('buttonBackgroundColor', e.target.value)}
                    className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.buttonBackgroundColor}
                    onChange={(e) => handleInputChange('buttonBackgroundColor', e.target.value)}
                    placeholder="#3B82F6"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Set the background color for buttons in section forms.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Button Text Color</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={formData.buttonTextColor}
                    onChange={(e) => handleInputChange('buttonTextColor', e.target.value)}
                    className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.buttonTextColor}
                    onChange={(e) => handleInputChange('buttonTextColor', e.target.value)}
                    placeholder="#FFFFFF"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Set the text color for buttons in section forms.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Hover Button Background Color</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={formData.hoverButtonBackgroundColor}
                    onChange={(e) => handleInputChange('hoverButtonBackgroundColor', e.target.value)}
                    className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.hoverButtonBackgroundColor}
                    onChange={(e) => handleInputChange('hoverButtonBackgroundColor', e.target.value)}
                    placeholder="#2563EB"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Set the background color when hovering over buttons in section forms.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Hover Button Text Color</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={formData.hoverButtonTextColor}
                    onChange={(e) => handleInputChange('hoverButtonTextColor', e.target.value)}
                    className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.hoverButtonTextColor}
                    onChange={(e) => handleInputChange('hoverButtonTextColor', e.target.value)}
                    placeholder="#FFFFFF"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Set the text color when hovering over buttons in section forms.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Apply button styling to section types</label>
                <p className="text-sm text-gray-500 mb-4">Choose which section types should inherit custom button colors.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {combinedSectionTypeOptions.map(({ value, label }) => (
                    <label
                      key={`button-${value}`}
                      className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 hover:border-blue-300 transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={formData.selectedButtonSectionTypes.includes(value)}
                        onChange={(e) => handleSelectionChange('selectedButtonSectionTypes', value, e.target.checked)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Preview */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
            <div className="border border-gray-200 rounded-2xl p-4 bg-white shadow-sm">
              <div
                className="rounded-2xl p-5 space-y-4 transition-colors duration-200 hover:shadow-md"
                style={{
                  backgroundColor: formData.backgroundColor,
                  color: formData.textColor
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = formData.hoverBackgroundColor;
                  e.currentTarget.style.color = formData.hoverTextColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = formData.backgroundColor;
                  e.currentTarget.style.color = formData.textColor;
                }}
              >
                <div className="space-y-2">
                  <div className="text-sm font-semibold">Lead Form Preview</div>
                  <div className="grid grid-cols-1 gap-3">
                    <div className="h-10 rounded-xl bg-white/70 border border-white/70" />
                    <div className="h-10 rounded-xl bg-white/70 border border-white/70" />
                    <div className="h-10 rounded-xl bg-white/70 border border-white/70" />
                  </div>
                </div>
                <button
                  className="w-full px-4 py-3 rounded-2xl font-semibold shadow-sm transition-colors duration-200"
                  style={{
                    backgroundColor: formData.buttonBackgroundColor,
                    color: formData.buttonTextColor
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = formData.hoverButtonBackgroundColor;
                    e.currentTarget.style.color = formData.hoverButtonTextColor;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = formData.buttonBackgroundColor;
                    e.currentTarget.style.color = formData.buttonTextColor;
                  }}
                >
                  Submit
                </button>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <FiLoader className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <FiSave className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
